import React from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingDown, ShieldAlert, ShieldCheck, Clock,
  ChevronRight, ArrowUpRight, DollarSign,
} from 'lucide-react';
import { useLevyOptimization } from '../../hooks/useSkillsUpgradingData';

// The full operational workqueue lives in Learning & Development; the dashboard
// is the at-a-glance awareness layer that deep-links into it (pre-filtered).
const WORKQUEUE_BASE = '/admin/learning-development?tab=levy-optimization';

const LevyOptimizationSection = () => {
  const { data: snapshot, isLoading, isError } = useLevyOptimization();

  // Non-HR users get a 403; render nothing rather than an error box.
  if (isError) return null;

  const summary = snapshot?.summary;

  return (
    <section className="space-y-6" aria-label="Levy Optimization">
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
          <div className="space-y-1.5 sm:space-y-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Levy Optimization</h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              Workers at risk of losing their lowered levy, and those eligible to move to a lower levy
            </p>
          </div>
          <Link
            to={WORKQUEUE_BASE}
            className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 flex-shrink-0"
          >
            Open workqueue <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="metrics-card h-full animate-pulse">
                <div className="metrics-card-content">
                  <div className="metrics-card-icon bg-gray-200" />
                  <div className="metrics-card-text space-y-2">
                    <div className="h-6 w-12 bg-gray-200 rounded" />
                    <div className="h-4 w-24 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : summary ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {/* At Risk → workqueue. Whole card is the tap target (no footer link). */}
            <Link
              to={WORKQUEUE_BASE}
              title="Registrations expired or expiring within 90 days"
              aria-label={`View levy workqueue — ${summary.at_risk_total} workers at risk`}
              className="metrics-card h-full w-full text-left transition-all hover:ring-2 hover:ring-red-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              <div className="metrics-card-content items-start">
                <div className={`metrics-card-icon ${summary.at_risk_total > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <ShieldAlert className={`w-4 h-4 sm:w-6 sm:h-6 ${summary.at_risk_total > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div className="metrics-card-text">
                  <p className={`metrics-card-value tabular-nums ${summary.at_risk_total > 0 ? 'text-red-600' : 'text-gray-900'}`}>{summary.at_risk_total}</p>
                  <p className="metrics-card-label">At Risk</p>
                  <div className="mt-1 space-y-0.5">
                    <p className="text-[11px] sm:text-xs text-red-600 truncate">{summary.at_risk_expired} expired</p>
                    <p className="text-[11px] sm:text-xs text-orange-600 truncate">{summary.at_risk_expiring_soon} expiring soon</p>
                  </div>
                </div>
              </div>
            </Link>

            {/* Expiring soon */}
            <Link
              to={`${WORKQUEUE_BASE}&status=expiring_soon`}
              title="Expiring within 90 days — renew via CET"
              aria-label={`View workers expiring within 90 days — ${summary.at_risk_expiring_soon}`}
              className="metrics-card h-full w-full text-left transition-all hover:ring-2 hover:ring-orange-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              <div className="metrics-card-content">
                <div className={`metrics-card-icon ${summary.at_risk_expiring_soon > 0 ? 'bg-orange-100' : 'bg-emerald-100'}`}>
                  <Clock className={`w-4 h-4 sm:w-6 sm:h-6 ${summary.at_risk_expiring_soon > 0 ? 'text-orange-600' : 'text-emerald-600'}`} />
                </div>
                <div className="metrics-card-text">
                  <p className={`metrics-card-value tabular-nums ${summary.at_risk_expiring_soon > 0 ? 'text-orange-600' : 'text-gray-900'}`}>{summary.at_risk_expiring_soon}</p>
                  <p className="metrics-card-label">
                    <span className="sm:hidden">Expiring soon</span>
                    <span className="hidden sm:inline">Expiring ≤90 days</span>
                  </p>
                </div>
              </div>
            </Link>

            {/* Opportunities → workqueue (opportunities view) */}
            <Link
              to={`${WORKQUEUE_BASE}&view=opportunities`}
              title="Basic-skilled workers eligible to upgrade to a lower levy"
              aria-label={`View upgrade opportunities — ${summary.opportunities_total}`}
              className="metrics-card h-full w-full text-left transition-all hover:ring-2 hover:ring-blue-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              <div className="metrics-card-content">
                <div className={`metrics-card-icon ${summary.opportunities_total > 0 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <ArrowUpRight className={`w-4 h-4 sm:w-6 sm:h-6 ${summary.opportunities_total > 0 ? 'text-blue-700' : 'text-gray-400'}`} />
                </div>
                <div className="metrics-card-text">
                  <p className="metrics-card-value tabular-nums">{summary.opportunities_total}</p>
                  <p className="metrics-card-label">Upgrade Opportunities</p>
                </div>
              </div>
            </Link>

            {/* Potential savings */}
            <div className="metrics-card h-full" title="Annual levy savings if eligible workers upgrade">
              <div className="metrics-card-content items-start">
                <div className="metrics-card-icon bg-emerald-100">
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
                <div className="metrics-card-text">
                  <p className="metrics-card-value tabular-nums">${(summary.opportunities_annual_savings || 0).toLocaleString()}</p>
                  <p className="metrics-card-label">
                    <span className="sm:hidden">Savings/yr</span>
                    <span className="hidden sm:inline">Potential Savings/yr</span>
                  </p>
                  {summary.opportunities_monthly_savings > 0 && (
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-1 tabular-nums truncate">${summary.opportunities_monthly_savings.toLocaleString()}/mo</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-10 text-center">
            <ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500">No levy-optimization data available yet.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default LevyOptimizationSection;
